import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { holidayService } from '../../services/holidayService';
import { locationService } from '../../services/locationService';
import { format } from 'date-fns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import ConfirmDialog from '../Common/ConfirmDialog';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#E65100',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #BF360C',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

// Generate year options (3 years back to 1 year forward) in descending order
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear + 1; y >= currentYear - 3; y--) {
    years.push(y);
  }
  return years;
};

// Month options
const monthOptions = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const HolidayList = () => {
  const navigate = useNavigate();
  const [holidays, setHolidays] = useState([]);
  const [filteredHolidays, setFilteredHolidays] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);

  const loadLocations = async () => {
    try {
      const data = await locationService.getActive();
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations:', err);
      setError('Failed to load locations');
    }
  };

  const loadHolidays = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await holidayService.getAll(filterLocation || null);
      setHolidays(data);
    } catch (err) {
      console.error('Error loading holidays:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load holidays');
    } finally {
      setLoading(false);
    }
  }, [filterLocation]);

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  // Apply year and month filters, then sort by year desc, date asc within year
  useEffect(() => {
    let filtered = [...holidays];

    if (filterYear) {
      filtered = filtered.filter((holiday) => {
        const holidayDate = new Date(holiday.date);
        return holidayDate.getFullYear() === parseInt(filterYear);
      });
    }

    if (filterMonth) {
      filtered = filtered.filter((holiday) => {
        const holidayDate = new Date(holiday.date);
        return holidayDate.getMonth() + 1 === parseInt(filterMonth);
      });
    }

    // Sort: year descending, location ascending, date ascending within location
    filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      const yearA = dateA.getFullYear();
      const yearB = dateB.getFullYear();

      // First sort by year descending
      if (yearA !== yearB) {
        return yearB - yearA;
      }
      // Within same year, sort by location ascending
      const locationCompare = a.location.localeCompare(b.location);
      if (locationCompare !== 0) {
        return locationCompare;
      }
      // Within same location, sort by date ascending
      return dateA.getTime() - dateB.getTime();
    });

    setFilteredHolidays(filtered);
  }, [holidays, filterYear, filterMonth]);

  const handleAddHoliday = () => {
    navigate('/holidays/new');
  };

  const handleDeleteClick = (holiday) => {
    setHolidayToDelete(holiday);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!holidayToDelete) return;
    try {
      await holidayService.delete(holidayToDelete.holiday_id);
      setSuccess('Holiday deleted successfully');
      loadHolidays();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete holiday');
      setTimeout(() => setError(''), 3000);
    } finally {
      setDeleteDialogOpen(false);
      setHolidayToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setHolidayToDelete(null);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!filteredHolidays || filteredHolidays.length === 0) return;

    const headers = ['SNo', 'Location', 'Date', 'Holiday Name'];

    const dataRows = filteredHolidays.map((holiday, index) => [
      index + 1,
      holiday.location,
      format(new Date(holiday.date), 'EEEE, MMMM dd, yyyy'),
      holiday.name || '-',
    ]);

    const wsData = [headers, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    headers.forEach((_, index) => {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: index });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          fill: { fgColor: { rgb: 'E65100' } },
          font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
          alignment: { horizontal: 'center', vertical: 'center' },
          border: { bottom: { style: 'medium', color: { rgb: '000000' } } },
        };
      }
    });

    dataRows.forEach((row, rowIndex) => {
      row.forEach((_, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: rowIndex + 1, c: colIndex });
        if (ws[cellRef]) {
          ws[cellRef].s = {
            fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'FFF3E0' } },
            alignment: { horizontal: 'left', vertical: 'center' },
          };
        }
      });
    });

    ws['!cols'] = [{ wch: 6 }, { wch: 15 }, { wch: 30 }, { wch: 25 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Holidays');
    XLSX.writeFile(wb, `Holidays_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!filteredHolidays || filteredHolidays.length === 0) return;

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.setTextColor(230, 81, 0);
    doc.text('Holiday Calendar', 14, 20);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 28);

    const headers = [['SNo', 'Location', 'Date', 'Holiday Name']];

    const data = filteredHolidays.map((holiday, index) => [
      index + 1,
      holiday.location,
      format(new Date(holiday.date), 'EEEE, MMMM dd, yyyy'),
      holiday.name || '-',
    ]);

    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      theme: 'grid',
      headStyles: {
        fillColor: [230, 81, 0],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 15 },
      },
      alternateRowStyles: { fillColor: [255, 243, 224] },
    });

    doc.save(`Holidays_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Page Header */}
      <Box sx={{
        mb: 3,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #E65100 0%, #FF8F00 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(230, 81, 0, 0.3)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <EventIcon sx={{ fontSize: 32 }} />
            <Box>
              <Typography variant="h5" fontWeight="bold">Holiday Calendar</Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Manage location-based holidays
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Export to PDF">
              <Button
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                onClick={exportToPDF}
                disabled={filteredHolidays.length === 0}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                PDF
              </Button>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <Button
                variant="contained"
                startIcon={<TableChartIcon />}
                onClick={exportToExcel}
                disabled={filteredHolidays.length === 0}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Excel
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddHoliday}
              sx={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.3)' },
              }}
            >
              Add Holiday
            </Button>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          label="Filter by Location"
          value={filterLocation}
          onChange={(e) => setFilterLocation(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Locations</MenuItem>
          {locations.map((location) => (
            <MenuItem key={location.location_id} value={location.location_name}>
              {location.location_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Filter by Year"
          value={filterYear}
          onChange={(e) => setFilterYear(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Years</MenuItem>
          {getYearOptions().map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Filter by Month"
          value={filterMonth}
          onChange={(e) => setFilterMonth(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">All Months</MenuItem>
          {monthOptions.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: 60 }} align="center">SNo</TableCell>
              <TableCell sx={headerCellStyle}>Location</TableCell>
              <TableCell sx={headerCellStyle}>Date</TableCell>
              <TableCell sx={headerCellStyle}>Holiday Name</TableCell>
              <TableCell align="center" sx={headerCellStyle}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHolidays.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  No holidays found
                </TableCell>
              </TableRow>
            ) : (
              filteredHolidays.map((holiday, index) => (
                <TableRow
                  key={holiday.holiday_id}
                  sx={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#FFF3E0',
                    '&:hover': { backgroundColor: '#FFE0B2' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell><strong>{holiday.location}</strong></TableCell>
                  <TableCell>
                    {format(new Date(holiday.date), 'EEEE, MMMM dd, yyyy')}
                  </TableCell>
                  <TableCell>{holiday.name || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteClick(holiday)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Holiday"
        message="Are you sure you want to delete this holiday?"
        details={holidayToDelete ? [
          { label: 'Holiday', value: holidayToDelete.name },
          { label: 'Date', value: format(new Date(holidayToDelete.date), 'MMMM dd, yyyy') },
          { label: 'Location', value: holidayToDelete.location },
        ] : []}
      />
    </Container>
  );
};

export default HolidayList;
